import { useContext, useEffect, useRef, useState } from 'react';
import {
  CalendarIcon,
  CloudDownloadIcon,
  CopyIcon,
  LocationIcon,
  LockIcon,
  PencilIcon,
  PlayOutlineIcon,
  PlusIcon,
  QrIcon,
  SendIcon,
  ThreeDots,
  TrashIcon,
} from 'assets/icons';
import { LocationMenu } from 'common/album-detail-modal/components/menus/location-menu';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { AlbumAddContentTypes, NotificationType } from 'common/enums';
import { DropDownListItem } from 'common/drop-down-list-item';
import { Menu } from 'common/menu';
import { ModalWarning } from 'common/modal-warning';
import { PrivateToggleItem } from 'common/private-toggle-item';
import { QrCodeCard } from 'common/qr-code-card';
import { ShareDialog } from 'common/share-dialog';
import { apiBaseURL } from 'config/constants';
import { appActions, AppContext } from 'context/app-context';
import { useClickOutside } from 'hooks/click-outside';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { AlbumInviteController } from 'networking/controllers/album-invite-controller';
import { AlbumDetailType } from 'networking/types/album-detail';
import { goToPage, RouteName } from 'routes';
import { getRouteFor } from 'routes/route-helpers';
import { CenterModal } from 'pages/album-view-mode/components/center-modal';
import { isPromptContent } from 'helpers/memories';
import { albumAccess, albumShareTarget, vaultAccess } from 'helpers/sharing';
import { VideoCallWaitingRoom } from 'pages/vault-detail/components/video-call-waiting-room';
import { NewMenuList } from 'pages/vault-detail/components/vault-top-bar/components/new-menu/new-menu-list';
import { SuggestedPromptsModal } from '../suggested-prompts-modal';
import { AlbumDateRange } from './album-date-range';
import styles from './album-header.module.scss';

interface AlbumHeaderProps {
  album: AlbumDetailType;
  vault: VaultType;
  onAddEntry: (mode?: AlbumAddContentTypes) => void;
  // Files and folders, through the import review (see common/upload-modal).
  onUpload: () => void;
  onView: () => void;
  onAlbumUpdated: (partial: Partial<AlbumDetailType>) => void;
  onAlbumDeleted: () => void;
  // Called after prompts were added from the New menu.
  onContentsChanged: () => void;
}

type SharedPerson = { key: string; name: string };

// Deterministic pastel palette (same scheme as common/user-initials).
const AVATAR_COLORS = [
  { background: '#ebf5fa', text: '#1b9fd8' },
  { background: '#ebf4f3', text: '#1d8c7f' },
  { background: '#fcf4ea', text: '#eb870e' },
  { background: '#faf7e9', text: '#d6b100' },
];

const initialsOf = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

const MAX_AVATARS = 5;

export const AlbumHeader = ({
  album,
  vault,
  onAddEntry,
  onUpload,
  onView,
  onAlbumUpdated,
  onAlbumDeleted,
  onContentsChanged,
}: AlbumHeaderProps) => {
  const { dispatch, state: appState } = useContext(AppContext);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showDatesEditor, setShowDatesEditor] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [members, setMembers] = useState<AlbumMemberType[]>([]);
  const [nameDraft, setNameDraft] = useState(album.name);
  const [saving, setSaving] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const newRef = useRef<HTMLDivElement>(null);
  const shareRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  useClickOutside(newRef, () => setShowNewMenu(false));
  useClickOutside(moreRef, () => setShowMoreMenu(false));

  const notify = (title: string, type: NotificationType, message?: string) =>
    dispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({
        show: true,
        title,
        message,
        type,
      }),
    });

  const ownerName = album.createdByUser?.name || vault.owner?.name || '';
  // What this person's role lets them do here (owner and editors: all).
  const access = albumAccess(album, vaultAccess(appState.data.user, vault));
  const isFullAccess = vaultAccess(appState.data.user, vault).fullAccess;

  // Who the album is shared with: only those who may share it can see that.
  const loadMembers = () =>
    access.canShare
      ? AlbumInviteController.getAlbumMembers(album.id)
          .then(setMembers)
          .catch(() => undefined)
      : Promise.resolve();

  useEffect(() => {
    loadMembers();
  }, [album.id, access.canShare]);

  // Printed QR codes that open this album (linked in the CMS).
  const qrCodes = album.qrCodes ?? [];

  // Everyone this album is shared with (invited or active).
  const sharedWith: SharedPerson[] = members.map((member) => ({
    key: `member-${member.id}`,
    name: member.name || member.email,
  }));

  const visibleAvatars = sharedWith.slice(0, MAX_AVATARS);
  const overflowCount = sharedWith.length - visibleAvatars.length;

  const inviterName = appState.data.user.name || '';
  const getContributorLink = () => {
    if (album.guestInviteToken) {
      return `${apiBaseURL}${getRouteFor(
        RouteName.AlbumViewMode,
        { albumId: album.id },
        {
          token: album.guestInviteToken,
          // Shown in the sign-up box: who invited them.
          inviter: btoa(unescape(encodeURIComponent(inviterName))),
        },
      )}`;
    }
    return '';
  };

  const handleRename = async () => {
    const newName = nameDraft.trim();
    if (!newName || newName === album.name) {
      setShowRenameModal(false);
      return;
    }
    try {
      setSaving(true);
      await VaultsController.updateAlbumMetadata(album.id, { name: newName });
      onAlbumUpdated({ name: newName });
      setShowRenameModal(false);
      notify('Album renamed', NotificationType.Success);
    } catch (error) {
      notify('The album could not be renamed', NotificationType.Error);
    } finally {
      setSaving(false);
    }
  };

  const handleDatesSave = async ({
    startDate,
    endDate,
  }: {
    startDate: string;
    endDate: string;
  }) => {
    try {
      await VaultsController.updateAlbumMetadata(album.id, {
        startDate,
        endDate,
      });
      onAlbumUpdated({ startDate, endDate });
      notify('Album dates updated', NotificationType.Success);
    } catch (error) {
      notify('The album dates could not be changed', NotificationType.Error);
      throw error;
    }
  };

  // The album's own location (each memory has its own too).
  const handleLocationSave = async (location: string) => {
    setShowLocationModal(false);
    try {
      await VaultsController.updateAlbumMetadata(album.id, { location });
      onAlbumUpdated({ location });
      notify('Album location updated', NotificationType.Success);
    } catch (error) {
      notify('The album location could not be changed', NotificationType.Error);
    }
  };

  // The backend zips the album and emails a link to the requester.
  const handleRequestDownload = async () => {
    try {
      await VaultsController.requestAlbumDownload({
        id: album.id,
        includePrivateClips: true,
      });
      setShowDownloadModal(false);
      notify('Download request submitted', NotificationType.Success);
    } catch (error) {
      notify(
        'The download could not be requested',
        NotificationType.Error,
        'Please try again.',
      );
    }
  };

  const handleTogglePrivate = async () => {
    const isPrivate = !album.isPrivate;
    try {
      setSavingPrivacy(true);
      await VaultsController.updateAlbum(album.id, isPrivate);
      onAlbumUpdated({ isPrivate });
      notify(
        isPrivate ? 'Album is now private' : 'Album is no longer private',
        NotificationType.Success,
      );
    } catch (error) {
      notify('The album could not be updated', NotificationType.Error);
    } finally {
      setSavingPrivacy(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteModal(false);
    try {
      await VaultsController.deleteAlbum(
        album.id,
        album.contents.reduce(
          (sum, content) => sum + (content.fileSize ?? 0),
          0,
        ),
      );
      notify('Album deleted', NotificationType.Success);
      onAlbumDeleted();
    } catch (error) {
      notify('The album could not be deleted', NotificationType.Error);
    }
  };

  const moreItems = [
    access.canEdit && (
      <DropDownListItem
        key="rename"
        leftIcon={<PencilIcon />}
        onClick={() => {
          setNameDraft(album.name);
          setShowMoreMenu(false);
          setShowRenameModal(true);
        }}
      >
        Rename album
      </DropDownListItem>
    ),
    access.canEdit && (
      <DropDownListItem
        key="dates"
        leftIcon={<CalendarIcon />}
        onClick={() => {
          setShowMoreMenu(false);
          setShowDatesEditor(true);
        }}
      >
        Change dates
      </DropDownListItem>
    ),
    access.canEdit && (
      <DropDownListItem
        key="location"
        leftIcon={<LocationIcon />}
        onClick={() => {
          setShowMoreMenu(false);
          setShowLocationModal(true);
        }}
      >
        Change location
      </DropDownListItem>
    ),
    // The download holds everything, private memories included.
    access.seesPrivate && (
      <DropDownListItem
        key="download"
        leftIcon={<CloudDownloadIcon />}
        onClick={() => {
          setShowMoreMenu(false);
          setShowDownloadModal(true);
        }}
      >
        Download album
      </DropDownListItem>
    ),
    isFullAccess && qrCodes.length > 0 && (
      <DropDownListItem
        key="qr"
        leftIcon={<QrIcon />}
        onClick={() => {
          setShowMoreMenu(false);
          setShowQrModal(true);
        }}
      >
        Show QR code
      </DropDownListItem>
    ),
    access.canEdit && (
      <PrivateToggleItem
        key="private"
        isPrivate={album.isPrivate}
        onToggle={handleTogglePrivate}
        disabled={savingPrivacy}
      />
    ),
    access.canDelete && (
      <DropDownListItem
        key="delete"
        leftIcon={<TrashIcon />}
        onClick={() => {
          setShowMoreMenu(false);
          setShowDeleteModal(true);
        }}
      >
        Delete album
      </DropDownListItem>
    ),
  ].filter(Boolean);

  const allActions = [
    {
      key: 'new',
      label: 'New',
      icon: <PlusIcon />,
      onClick: () => setShowNewMenu((prev) => !prev),
      ref: newRef,
      popover: showNewMenu && (
        <NewMenuList
          showAlbumSection={false}
          className={styles.newMenu}
          onClose={() => setShowNewMenu(false)}
          onUpload={onUpload}
          onCreateNote={() => onAddEntry(AlbumAddContentTypes.write)}
          onCreateAudio={() => onAddEntry(AlbumAddContentTypes.audio)}
          onCreateVideo={() => onAddEntry(AlbumAddContentTypes.video)}
          onCreateVideoCall={
            isFullAccess ? () => setShowVideoCall(true) : undefined
          }
          onNewPrompt={
            access.canCreatePrompt ? () => setShowPrompts(true) : undefined
          }
        />
      ),
    },
    {
      key: 'view',
      label: 'View',
      icon: <PlayOutlineIcon />,
      onClick: onView,
    },
    {
      key: 'move',
      label: 'Move',
      icon: <CopyIcon />,
      // Same entry point as "Move Memories" in the customer menu.
      onClick: () => goToPage(RouteName.MoveMemories, { id: vault.id }),
    },
    {
      key: 'share',
      label: 'Share',
      icon: <SendIcon />,
      onClick: () => setShowShare(true),
      ref: shareRef,
      popover: null,
    },
    {
      key: 'more',
      label: 'More',
      icon: <ThreeDots className={styles.moreIcon} />,
      onClick: () => setShowMoreMenu((prev) => !prev),
      ref: moreRef,
      popover: showMoreMenu && (
        <Menu position="" className={styles.moreMenu}>
          {moreItems}
        </Menu>
      ),
    },
  ];
  // Only what this person's role allows.
  const actions = allActions.filter(
    (action) =>
      (action.key !== 'new' || access.canUpload) &&
      (action.key !== 'share' || access.canShare) &&
      // Moving memories between albums is the owner's and editors'.
      (action.key !== 'move' || isFullAccess) &&
      (action.key !== 'more' || moreItems.length > 0),
  );

  return (
    <div className={styles.header}>
      <h1 className={styles.title}>{album.name}</h1>

      <div className={styles.datesRow}>
        <AlbumDateRange
          startDate={album.startDate}
          endDate={album.endDate}
          onSave={handleDatesSave}
          open={showDatesEditor}
          onOpenChange={setShowDatesEditor}
          readOnly={!access.canEdit}
        />
        {access.canEdit ? (
          <button
            type="button"
            className={styles.locationButton}
            onClick={() => setShowLocationModal(true)}
            title="Change the album's location"
          >
            <LocationIcon />
            {album.location || 'Add location'}
          </button>
        ) : (
          album.location && (
            <span className={styles.locationButton}>
              <LocationIcon />
              {album.location}
            </span>
          )
        )}
      </div>

      <div className={styles.metaRow}>
        {ownerName && <span className={styles.metaText}>{ownerName}</span>}
        {ownerName && <span className={styles.metaDot} />}
        <span className={styles.visibility}>
          {album.isPrivate ? 'Private' : 'Shared'}
          {album.isPrivate && <LockIcon className={styles.lockIcon} />}
        </span>
      </div>

      {access.canShare && sharedWith.length > 0 && (
        <div className={styles.avatarsRow}>
          <div className={styles.avatars}>
            {visibleAvatars.map((person, index) => {
              const color =
                AVATAR_COLORS[person.name.length % AVATAR_COLORS.length];
              return (
                <button
                  key={person.key}
                  type="button"
                  className={styles.avatar}
                  title={person.name}
                  aria-label={`Share album — ${person.name}`}
                  onClick={() => setShowShare(true)}
                  style={{
                    backgroundColor: color.background,
                    color: color.text,
                    zIndex: visibleAvatars.length - index,
                  }}
                >
                  {initialsOf(person.name)}
                </button>
              );
            })}
            {overflowCount > 0 && (
              <button
                type="button"
                className={styles.avatarOverflow}
                aria-label={`Share album — ${overflowCount} more`}
                onClick={() => setShowShare(true)}
              >
                +{overflowCount}
              </button>
            )}
          </div>
          <div className={styles.avatarAddWrap}>
            <button
              type="button"
              className={styles.avatarAdd}
              aria-label="Add user"
              onClick={() => setShowShare(true)}
            >
              <PlusIcon />
            </button>
          </div>
        </div>
      )}

      <div className={styles.actionsRow}>
        {actions.map((action) => (
          <div key={action.key} className={styles.action} ref={action.ref}>
            <div className={styles.actionButtonWrap}>
              <button
                type="button"
                className={`${styles.actionButton} ${
                  action.popover ? styles.actionButtonActive : ''
                }`}
                aria-label={action.label}
                onClick={action.onClick}
              >
                {action.icon}
              </button>
              {action.popover}
            </div>
            <span className={styles.actionLabel}>{action.label}</span>
          </div>
        ))}
      </div>

      {showRenameModal && (
        <CenterModal
          onClose={() => setShowRenameModal(false)}
          className={styles.renameModal}
        >
          <div className={styles.renameBody}>
            <span className={styles.renameTitle}>Rename album</span>
            <input
              type="text"
              className={styles.renameInput}
              value={nameDraft}
              maxLength={120}
              placeholder="Album name"
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRename();
              }}
              /* eslint-disable-next-line jsx-a11y/no-autofocus */
              autoFocus
            />
            <div className={styles.renameActions}>
              <Button
                buttonStyle={ButtonStyle.SecondaryGray}
                buttonSize={ButtonSize.Medium}
                onClick={() => setShowRenameModal(false)}
              >
                Cancel
              </Button>
              <Button
                buttonStyle={ButtonStyle.Primary}
                buttonSize={ButtonSize.Medium}
                disabled={saving || !nameDraft.trim()}
                onClick={handleRename}
              >
                Save
              </Button>
            </div>
          </div>
        </CenterModal>
      )}

      {showDownloadModal && (
        <ModalWarning
          title="Download album"
          icon={<CloudDownloadIcon />}
          iconVariant="bordered"
          content={
            <div>
              A zip file with everything in <strong>{album.name}</strong> will
              be sent to{' '}
              <strong>{appState.data.user?.email || 'your email'}</strong>{' '}
              within 24 hours.
            </div>
          }
          successButtonText="Request download"
          successStyleButton={ButtonStyle.PrimaryFilled}
          successButtonSize={ButtonSize.Large}
          closeButtonText="Cancel"
          closeFn={() => setShowDownloadModal(false)}
          successFn={handleRequestDownload}
        />
      )}

      {showLocationModal && (
        <CenterModal onClose={() => setShowLocationModal(false)}>
          <div className={styles.locationBody}>
            <LocationMenu renderInline addLocation={handleLocationSave} />
          </div>
        </CenterModal>
      )}

      {showQrModal && (
        <CenterModal
          onClose={() => setShowQrModal(false)}
          className={styles.renameModal}
        >
          <div className={styles.renameBody}>
            <span className={styles.renameTitle}>QR code</span>
            {qrCodes.map((qrCode) => (
              <QrCodeCard key={qrCode.id} qrCode={qrCode} />
            ))}
          </div>
        </CenterModal>
      )}

      {showDeleteModal && (
        <ModalWarning
          title="Delete album"
          content="Are you sure you want to delete this Album and all of its entries? This action cannot be undone."
          successButtonText="Delete"
          successStyleButton={ButtonStyle.DestructivePrimary}
          closeFn={() => setShowDeleteModal(false)}
          successFn={handleDelete}
        />
      )}

      {showShare && (
        <ShareDialog
          target={albumShareTarget(vault.id, album)}
          contributorLink={getContributorLink() || undefined}
          onMakeVisible={async () => {
            await VaultsController.updateAlbum(album.id, false);
            onAlbumUpdated({ isPrivate: false });
          }}
          onClose={() => {
            setShowShare(false);
            loadMembers();
          }}
        />
      )}

      {showVideoCall && (
        <VideoCallWaitingRoom
          vault={vault}
          onClose={() => setShowVideoCall(false)}
        />
      )}

      {showPrompts && (
        <SuggestedPromptsModal
          vaultId={vault.id}
          albumId={album.id}
          existingPromptNames={(album.contents ?? [])
            .filter(isPromptContent)
            .map((content) => content.name || '')}
          onClose={() => setShowPrompts(false)}
          onAdded={onContentsChanged}
        />
      )}
    </div>
  );
};
